import React from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {adminProduct} from '../../types/product';
import {ReactComponent as SettingsIcon} from '../../assets/icons/settings-icon.svg';
import {ReactComponent as EditIcon} from '../../assets/icons/edit-icon.svg';
import {ReactComponent as DeleteIcon} from '../../assets/icons/delete-icon.svg';
import {ReactComponent as VideoBadgeIcon} from '../../assets/icons/video-badge-icon.svg';
import {ReactComponent as VideoBadgeNullIcon} from '../../assets/icons/video-badge-icon2.svg';
import {ReactComponent as ArchivIcon} from '../../assets/icons/archiv-icon.svg';
import {ReactComponent as InArchivIcon} from '../../assets/icons/in-archiv-icon.svg';
import {ReactComponent as FromArchivIcon} from '../../assets/icons/from-archiv-icon.svg';
import {ReactComponent as SailIcon} from '../../assets/icons/sail-icon.svg';
import styles from './ProductsTable.module.scss';

interface ProductsTableProps {
    products: adminProduct[];
    videoStatusCounter?: {
        archiveCount: number;
        forsaleCount: number;
    };
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    onArchiv: (id: string, status: string) => void;
    MotionWrapper?: React.ElementType;
}

export const ProductsTable: React.FC<ProductsTableProps> = (
    {
        products,
        onEdit,
        onDelete,
        onArchiv,
        MotionWrapper = motion.div,
    }) => {
    const handleEdit = (id: string) => (e: React.MouseEvent) => {
        e.stopPropagation();
        onEdit(id);
    };

    const handleDelete = (id: string) => (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(id);
    };

    const handleArchiv = (id: string, status: string) => (e: React.MouseEvent) => {
        e.stopPropagation();
        onArchiv(id, status);
    };

    return (
        <div className={styles.productsTableContainer}>
            <div className={styles.tableHeader}>
                <div className={`${styles.columnHeader} ${styles.photoColumn}`}><span
                    className={styles.headerText}>Фото</span></div>
                <div className={`${styles.columnHeader} ${styles.nameColumn}`}><span className={styles.headerText}>Название товара</span>
                </div>
                <div className={`${styles.columnHeader} ${styles.contentColumn}`}><span
                    className={styles.headerText}>Содержание</span></div>
                <div className={`${styles.columnHeader} ${styles.authorColumn}`}><span className={styles.headerText}>Автор курса</span>
                </div>
                <div className={`${styles.columnHeader} ${styles.statusColumn}`}><span
                    className={styles.headerText}>Статус</span></div>
                <div className={`${styles.columnHeader} ${styles.priceColumn}`}><span className={styles.headerText}>Ваша цена</span>
                </div>
                <div className={`${styles.columnHeader} ${styles.actionsColumn}`}>
                    <div className={styles.settingsIcon}><SettingsIcon/></div>
                </div>
            </div>

            <AnimatePresence>
                {products.map((product) => (
                    <MotionWrapper
                        key={product.id}
                        initial={{opacity: 0, y: -10}}
                        animate={{opacity: 1, y: 0}}
                        exit={{opacity: 0, y: 10}}
                        transition={{duration: 0.2}}
                        className={styles.tableRow}
                    >
                        <div className={`${styles.tableCell} ${styles.photoCell}`}>
                            <div className={styles.photoWrapper}>
                                <div className={styles.photoBackground}/>
                                <div
                                    className={styles.photoImage}
                                    style={{backgroundImage: product.image ? `url(${product.image})` : 'none'}}
                                >
                                    <div className={styles.photoCircle}>
                                        <span className={styles.photoCircleText}>+{product.images_count}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={`${styles.tableCell} ${styles.nameCell}`}>
                            <span>{product.title}</span>
                        </div>

                        <div className={`${styles.tableCell} ${styles.contentCell}`}>
                            <div className={styles.contentBadge}>
                                {product.videos_count > 0 ? (
                                    <>
                                        <VideoBadgeIcon/>
                                        <div className={styles.contentCount}>{product.videos_count} видео</div>
                                    </>
                                ) : (
                                    <VideoBadgeNullIcon/>
                                )}
                                <div className={product.description ? styles.hasDescription : styles.noDescription}>
                                    {product.description ? 'Есть описание' : 'Нет описания'}
                                </div>
                            </div>
                        </div>

                        <div className={`${styles.tableCell} ${styles.authorCell}`}>
                            <span>{product.author}</span>
                        </div>

                        <div className={`${styles.tableCell} ${styles.statusCell}`}>
                            {product.status === 'forsale' ? <SailIcon/> : <ArchivIcon/>}
                        </div>

                        <div className={`${styles.tableCell} ${styles.priceCell}`}>
                            <span>{product.price.toLocaleString('ru-RU')} ₽</span>
                            {product.discount ? (
                                <span className={styles.discountPrice}>{product.discount.toLocaleString('ru-RU')} ₽ по скидке</span>
                            ) : (
                                <span className={styles.noDiscount}>Нет скидки</span>
                            )}
                        </div>

                        <div className={`${styles.tableCell} ${styles.actionsCell}`}>
                            <div className={styles.actionsContainer}>
                                <button onClick={handleEdit(product.id)}
                                        className={`${styles.iconButton} ${styles.hasTooltip}`}
                                        data-tooltip="Редактировать">
                                    <EditIcon/>
                                </button>
                                <button onClick={handleDelete(product.id)}
                                        className={`${styles.iconButton} ${styles.hasTooltip}`} data-tooltip="Удалить">
                                    <DeleteIcon/>
                                </button>
                                {product.status === 'forsale' ? (
                                    <button onClick={handleArchiv(product.id, product.status)}
                                            className={`${styles.iconButton} ${styles.hasTooltip}`}
                                            data-tooltip="В архив">
                                        <InArchivIcon/>
                                    </button>
                                ) : (
                                    <button onClick={handleArchiv(product.id, product.status)}
                                            className={`${styles.iconButton} ${styles.hasTooltip}`}
                                            data-tooltip="Из архива">
                                        <FromArchivIcon/>
                                    </button>
                                )}
                            </div>
                        </div>
                    </MotionWrapper>
                ))}
            </AnimatePresence>
        </div>
    );
};
